<template>
  <bm-control v-bind="$attrs">
    <template #readOnly="{ dataQa }">
      <div v-if="imgSrc === null">-</div>
      <v-img
        v-else
        :src="'data:image;base64,' + imgSrc.fileContentsBase64"
        contain
        :max-width="imageWidth"
        :data-qa="dataQa"
      ></v-img>
    </template>

    <template #default="{ dataQa }">
      <v-file-input
        ref="imageUploader"
        class="mt-3"
        :value="placeholderFile"
        placeholder=" "
        color="brand02"
        :loading="loading"
        :rules="rules"
        prepend-icon="add_photo_alternate"
        :accept="fileTypes"
        :label="undefined"
        :data-qa="dataQa"
        @change="newImage($event)"
      >
      </v-file-input>
      <v-img
        v-if="imgSrc !== null"
        :src="'data:image;base64,' + imgSrc.fileContentsBase64"
        contain
        :max-width="imageWidth"
      ></v-img>
    </template>
  </bm-control>
</template>

<script lang="ts">
import BmControl from "@/components/controls/input/BmControl.vue";
import Guid from "@/app/types/common/guid";
import api from "@/app/api";
import { EnumFileType } from "@/app/services/generated/enums/enumFileType.generated";
import { FileBase64, fileService } from "@/app/services/generated/fileService";
import type { PropType } from "vue";
import { defineComponent } from "vue";
import { Base64 } from "@/app/base64";
import { acceptedImageTypes } from "@/app/imageUtils";

export default defineComponent({
  name: "BmImageUploaderById",
  components: { BmControl },
  props: {
    value: {
      type: Object as PropType<Guid | null>,
      required: false,
    },
    imageWidth: {
      type: Number,
      default: 125,
    },
    maxFileMBytes: {
      type: Number,
      default: 2,
    },
    resizeMaxFileBytes: {
      type: Number,
      required: false,
    },
  },
  data() {
    return {
      imgSrc: null as FileBase64 | null,
      placeholderFile: null as File | null,
      loading: false as boolean,
      fileTypes: acceptedImageTypes,
    };
  },
  computed: {
    rules(): Function[] {
      return [
        (value: File): boolean | string =>
          !value ||
          value.size <= this.maxFileMBytes * 1000000 ||
          this.$m.file_too_big + " " + this.maxFileMBytes + "MB",
        (value: File): boolean | string =>
          !value ||
          value.type === "image/internal" ||
          this.fileTypes.includes(value.type) ||
          this.$m.validation_incorrect_file_type,
      ];
    },
  },
  watch: {
    value: {
      // @ts-ignore
      handler: "onValueChange",
      immediate: true,
    },
  },
  methods: {
    newImage(file: File) {
      try {
        if (!file) {
          this.$emit("input", null);
          return;
        }
        // Image internal is a file with a name and no contents to display in the uploader box.
        // Image internal is set to be able to clear the current image without triggering a change.
        if (file.type === "image/internal") {
          return;
        }

        if (!(this.$refs.imageUploader! as any).validate()) {
          return;
        }

        this.loading = true;
        const fileReader = new FileReader();
        fileReader.onload = async () => {
          if (fileReader.result instanceof ArrayBuffer) {
            const fileUpload = new FileBase64();
            fileUpload.fileContentsBase64 = Base64.fromUint8Array(
              new Uint8Array(fileReader.result)
            );
            fileUpload.fileType = EnumFileType.image;
            fileUpload.id = Guid.create();
            fileUpload.fileName = file.name;
            await fileService.saveFileBase64(
              api.tenantId,
              fileUpload,
              this.resizeMaxFileBytes ?? null
            );
            this.$emit("input", fileUpload.id);
          } else {
            throw Error(this.$m.keys.product_upload_unexpected_file.value);
          }
        };
        fileReader.readAsArrayBuffer(file);
      } catch (e) {
        throw Error(this.$m.keys.product_upload_error + " " + e);
      }
    },
    async onValueChange(val: Guid | null) {
      this.loading = false;
      if (val !== null) {
        try {
          this.imgSrc = await fileService.getFileBase64(api.tenantId, val);
        } catch (e: any) {
          throw Error(this.$m.keys.product_image_retrieval_error + e);
        }

        if (this.imgSrc !== null) {
          // file with a name and no content. Allows file to appear on refresh and not trigger another upload
          this.placeholderFile = new File([], this.imgSrc.fileName, {
            type: "image/internal",
          });
        }
      } else {
        this.imgSrc = null;
        this.placeholderFile = null;
      }
    },
  },
});
</script>

<style scoped lang="sass">
@import "src/sass/typography"

:deep(.v-input)
  @include bm-field
</style>
